(() => {
  const animations = {
    fade: {
      out: [{ opacity: 1 }, { opacity: 0 }],
      in: [{ opacity: 0 }, { opacity: 1 }]
    },
    'slide-left': {
      out: [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0.35, transform: 'translateX(-100%)' }],
      in: [{ opacity: 0.35, transform: 'translateX(100%)' }, { opacity: 1, transform: 'translateX(0)' }]
    },
    'slide-right': {
      out: [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0.35, transform: 'translateX(100%)' }],
      in: [{ opacity: 0.35, transform: 'translateX(-100%)' }, { opacity: 1, transform: 'translateX(0)' }]
    },
    'slide-up': {
      out: [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0.35, transform: 'translateY(-100%)' }],
      in: [{ opacity: 0.35, transform: 'translateY(100%)' }, { opacity: 1, transform: 'translateY(0)' }]
    },
    zoom: {
      out: [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.08)' }],
      in: [{ opacity: 0, transform: 'scale(.92)' }, { opacity: 1, transform: 'scale(1)' }]
    },
    blur: {
      out: [{ opacity: 1, filter: 'blur(0)' }, { opacity: 0, filter: 'blur(16px)' }],
      in: [{ opacity: 0, filter: 'blur(16px)' }, { opacity: 1, filter: 'blur(0)' }]
    },
    flip: {
      out: [{ opacity: 1, transform: 'perspective(1200px) rotateY(0deg)' }, { opacity: 0, transform: 'perspective(1200px) rotateY(-8deg)' }],
      in: [{ opacity: 0, transform: 'perspective(1200px) rotateY(8deg)' }, { opacity: 1, transform: 'perspective(1200px) rotateY(0deg)' }]
    }
  };

  const init = (root) => {
    if (!root || root.dataset.sliderReady === 'true') return;
    const slides = [...root.querySelectorAll('[data-slide]')];
    if (!slides.length) return;
    root.dataset.sliderReady = 'true';

    const dots = [...root.querySelectorAll('[data-slide-dot]')];
    const prev = root.querySelector('[data-slider-prev]');
    const next = root.querySelector('[data-slider-next]');
    const progress = root.querySelector('[data-slider-progress]');
    const autoplay = root.dataset.autoplay === 'true';
    const pauseHover = root.dataset.pauseHover === 'true';
    const swipeEnabled = root.dataset.swipe === 'true';
    const interval = Math.max(3000, Number(root.dataset.interval) || 5000);
    const transition = root.dataset.transition || 'fade';
    const duration = Math.max(0, Number(root.dataset.transitionDuration) || 600);
    const easing = root.dataset.transitionEasing || 'ease';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;
    let animating = false;
    let touchStartX = 0;
    let touchStartY = 0;

    const syncUi = () => {
      slides.forEach((slide, i) => {
        const active = i === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        const active = i === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    const resetProgress = () => {
      if (!progress) return;
      progress.style.animation = 'none';
      void progress.offsetWidth;
      if (autoplay && !reducedMotion) {
        progress.style.animation = `gettmo-slider-progress ${interval}ms linear forwards`;
      }
    };

    const show = async (index, direction = 1, immediate = false) => {
      const nextIndex = (index + slides.length) % slides.length;
      if (nextIndex === current || animating) return;
      const oldSlide = slides[current];
      const newSlide = slides[nextIndex];
      const effectName = transition === 'slide-left' && direction < 0 ? 'slide-right' : transition === 'slide-right' && direction < 0 ? 'slide-left' : transition;
      const effect = animations[effectName];

      animating = true;
      newSlide.classList.add('is-active', 'is-entering');
      newSlide.setAttribute('aria-hidden', 'false');
      oldSlide.classList.add('is-leaving');

      if (!immediate && !reducedMotion && transition !== 'none' && effect && typeof newSlide.animate === 'function') {
        const options = { duration, easing, fill: 'both' };
        const outAnimation = oldSlide.animate(effect.out, options);
        const inAnimation = newSlide.animate(effect.in, options);
        await Promise.allSettled([outAnimation.finished, inAnimation.finished]);
        outAnimation.cancel();
        inAnimation.cancel();
      }

      current = nextIndex;
      oldSlide.classList.remove('is-active', 'is-leaving');
      oldSlide.setAttribute('aria-hidden', 'true');
      newSlide.classList.remove('is-entering');
      syncUi();
      animating = false;
      resetProgress();
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = undefined;
      if (progress) progress.style.animationPlayState = 'paused';
    };

    const start = () => {
      stop();
      if (autoplay && slides.length > 1 && !reducedMotion) {
        resetProgress();
        timer = window.setInterval(() => show(current + 1, 1), interval);
      }
    };

    prev?.addEventListener('click', () => { show(current - 1, -1); start(); });
    next?.addEventListener('click', () => { show(current + 1, 1); start(); });
    dots.forEach((dot) => dot.addEventListener('click', () => {
      const target = Number(dot.dataset.slideDot);
      show(target, target >= current ? 1 : -1);
      start();
    }));

    if (pauseHover) {
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
    }
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { show(current - 1, -1); start(); }
      if (event.key === 'ArrowRight') { show(current + 1, 1); start(); }
    });

    if (swipeEnabled) {
      root.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0].clientX;
        touchStartY = event.changedTouches[0].clientY;
      }, { passive: true });
      root.addEventListener('touchend', (event) => {
        const dx = event.changedTouches[0].clientX - touchStartX;
        const dy = event.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          show(current + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
          start();
        }
      }, { passive: true });
    }

    syncUi();
    start();

    root._gettmoShowSlide = (index) => {
      stop();
      show(index, index >= current ? 1 : -1, true);
    };
  };

  const initAll = () => document.querySelectorAll('[data-gettmo-slider]').forEach(init);
  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', (event) => init(event.target.querySelector('[data-gettmo-slider]')));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-gettmo-slider]');
    if (!root) return;
    const slides = [...root.querySelectorAll('[data-slide]')];
    const index = slides.indexOf(event.target.closest('[data-slide]'));
    if (index >= 0 && typeof root._gettmoShowSlide === 'function') root._gettmoShowSlide(index);
  });
})();
